<script>
import gql from 'graphql-tag'
import { graphqlFragmentValidator } from '@/utils/propValidation'

export const PAGINATOR_INFO_FRAGMENT = gql`
  fragment PaginatorInfoFragment on PaginatorInfo {
    currentPage
    firstItem
    lastItem
    perPage
    total
  }
`

export default {
  name: 'ProvidePagination',
  fragments: {
    pagination: PAGINATOR_INFO_FRAGMENT,
  },
  props: {
    records: {
      type: Array,
      required: true,
    },
    page: {
      type: Number,
      default: 0,
    },
    perPage: {
      type: Number,
      default: 10,
    },
    totalItems: {
      type: Number,
      default: -1,
    },
    paginatorInfo: {
      type: Object,
      default: undefined,
      validator: graphqlFragmentValidator(PAGINATOR_INFO_FRAGMENT),
    },
  },
  emits: ['page-update', 'per-page-update', 'change'],
  data() {
    return {
      page_: this.page,
      perPage_: this.perPage,
    }
  },
  computed: {
    itemsPerPage: {
      get() {
        return this.paginatorInfo?.perPage ?? this.perPage_
      },
      set(perPage) {
        this.perPage_ = perPage
        this.$emit('per-page-update', perPage)
      },
    },
    activePage: {
      get() {
        // gql counts from one whereas we count from 0, therefore to handle
        // this uncertainty we have the +- to make things a big more elegant
        return (this.paginatorInfo?.currentPage ?? this.page_ + 1) - 1
      },
      set(page) {
        this.page_ = page
        this.$emit('page-update', page)
      },
    },
    fromItem() {
      return (
        this.paginatorInfo?.firstItem ??
        Math.min(this.activePage * this.itemsPerPage + 1, this.total)
      )
    },
    toItem() {
      return (
        this.paginatorInfo?.lastItem ??
        Math.min((this.activePage + 1) * this.itemsPerPage, this.total)
      )
    },
    paginated() {
      return this.paginatorInfo === undefined
        ? this.records.slice(this.fromItem - 1, this.toItem)
        : this.records
    },
    total() {
      return this.totalItems === -1
        ? (this.paginatorInfo?.total ?? this.records.length)
        : this.totalItems
    },
    totalPages() {
      return Math.ceil(this.total / this.itemsPerPage)
    },
    pages() {
      const p = []
      for (const page of Array(this.totalPages).keys()) {
        p.push({
          active: page === this.activePage,
          pageNumber: page + 1,
          goto: () => this.changePage(page),
        })
      }
      return p
    },
    next() {
      const f = () => {
        this.activePage = Math.min(this.activePage + 1, this.totalPages - 1)
      }

      f.active = this.activePage === this.totalPages - 1
      return f
    },
    prev() {
      const f = () => {
        this.activePage = Math.max(this.activePage - 1, 0)
      }
      f.active = this.activePage === 0
      return f
    },
  },
  watch: {
    page() {
      this.page_ = this.page
    },
    records: {
      deep: true,
      handler() {
        this.page_ = 0 // If we see any change to the data we have to reset the page to the first
      },
    },
    perPage() {
      this.activePage = 0
    },
  },
  methods: {
    changePage(page) {
      if (this.total > page * this.itemsPerPage) {
        this.activePage = page
      }
    },
    changePerPage(perPage) {
      this.itemsPerPage = perPage
    },
  },
  render() {
    this.$emit('change', this.paginated)
    return this.$slots.default({
      records: this.paginated, // Paginated records
      activePage: this.activePage + 1, // Current page we are on
      totalPages: this.totalPages,
      totalItems: this.total, // Obvious
      fromItem: this.fromItem,
      toItem: this.toItem,
      perPage: this.itemsPerPage,
      pages: this.pages, // List of buttons that can be rendered // might need to be padded somehow
      next: this.next,
      prev: this.prev,
      changePage: this.changePage,
      changePerPage: this.changePerPage,
    })
  },
}
</script>
